import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Grid,
  Alert,
  Button
} from '@mui/material';
import { PendingActions, ArrowBack } from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const karatOptions = ['22K', '24K', '22/22K', '24/24K', '18K', 'PT95', 'WG'];

const PendingList = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [karigars, setKarigars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [karigarFilter, setKarigarFilter] = useState('');
  const [karatFilter, setKaratFilter] = useState([]);
  const [requiredDateFilter, setRequiredDateFilter] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (karigarFilter) params.append('karigar', karigarFilter);
      if (karatFilter.length > 0) params.append('karat', karatFilter.join(','));
      if (requiredDateFilter) params.append('requiredDate', requiredDateFilter);

      // Use different API based on user role
      const karigarApiUrl = user?.role === 0 ? '/api/karigar/all' : '/api/karigar';
      
      // First test the database connection
      console.log('Testing database connection...');
      try {
        const dbTestRes = await fetch((process.env.REACT_APP_API_URL || '') + '/api/karigar/db-test');
        const dbTestData = await dbTestRes.json();
        console.log('Database test response:', dbTestData);
      } catch (dbTestErr) {
        console.error('Database test error:', dbTestErr);
      }
      
      // Then test the basic karigar API
      console.log('Testing basic karigar API...');
      try {
        const testRes = await api.get('/api/karigar/test');
        console.log('Test API response:', testRes.data);
      } catch (testErr) {
        console.error('Test API error:', testErr);
      }
      
      const [pendingRes, karigarsRes] = await Promise.all([
        api.get(`/api/karigar/pending-list?${params.toString()}`),
        api.get(karigarApiUrl)
      ]);
      
      console.log('User role:', user?.role, 'Karigar API URL:', karigarApiUrl);
      console.log('Karigars fetched:', karigarsRes.data);
      setItems(pendingRes.data.items || pendingRes.data);
      setKarigars(karigarsRes.data);
    } catch (err) {
      setError('Failed to fetch data');
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [karigarFilter, karatFilter, requiredDateFilter]);

  const handleKaratChange = (event) => {
    const value = event.target.value;
    setKaratFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <Container maxWidth="xl" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => window.history.back()} variant="outlined" color="primary">Back</Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <PendingActions sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" color="primary">
            Pending List
          </Typography>
        </Box>

        {/* Filters */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Karigar</InputLabel>
              <Select
                value={karigarFilter}
                label="Karigar"
                onChange={(e) => setKarigarFilter(e.target.value)}
              >
                <MenuItem value="">All Karigars</MenuItem>
                {karigars.map(karigar => (
                  <MenuItem key={karigar.abbreviation} value={karigar.abbreviation}>
                    {karigar.abbreviation} - {karigar.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Karat</InputLabel>
              <Select
                multiple
                value={karatFilter}
                onChange={handleKaratChange}
                input={<OutlinedInput label="Karat" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {karatOptions.map((karat) => (
                  <MenuItem key={karat} value={karat}>
                    <Checkbox checked={karatFilter.indexOf(karat) > -1} />
                    <ListItemText primary={karat} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Required Date (Before)"
              type="date"
              value={requiredDateFilter}
              onChange={(e) => setRequiredDateFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                Total Items: {items.length}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : items.length === 0 ? (
          <Alert severity="info">No pending items found with the current filters.</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Delivery Date</TableCell>
                  <TableCell>Required Date</TableCell>
                  <TableCell>Karat</TableCell>
                  <TableCell>Bill Name</TableCell>
                  <TableCell>Bill Number</TableCell>
                  <TableCell>Issue Date</TableCell>
                  <TableCell>Item Name</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Pcs/Pair/Set</TableCell>
                  <TableCell>Weight</TableCell>
                  <TableCell>Karigar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={`${item.orderId}-${item.itemId}-${index}`}>
                    <TableCell>{formatDate(item.deliveryDate)}</TableCell>
                    <TableCell>{formatDate(item.requiredDate)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={item.karat} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{item.billName}</TableCell>
                    <TableCell>{item.billNumber}</TableCell>
                    <TableCell>{item.issueDate ? formatDate(item.issueDate) : 'Not Issued'}</TableCell>
                    <TableCell>{item.itemName}</TableCell>
                    <TableCell>{item.quantity}</TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell>{item.itemWeight}</TableCell>
                    <TableCell>{item.karigarAbbreviation}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default PendingList; 